import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';

export type TranslateLangCode =
  | 'ru'
  | 'en'
  | 'hi'
  | 'ch'
  | 'tu'
  | 'pt'
  | 'sp'
  | 'ar'
  | 'ps'
  | 'de'
  | 'fr'
  | 'it';

@Injectable({ providedIn: 'root' })
export class TranslateService {
  private readonly storageKey = 'gt-lang';
  private readonly sourceLang = 'ru';
  private readonly cookieMaxAge = 365 * 24 * 60 * 60;
  private bootstrapInitialized = false;

  private readonly googleLangMap: Record<TranslateLangCode, string> = {
    ru: 'ru',
    en: 'en',
    hi: 'hi',
    ch: 'zh-TW',
    tu: 'tr',
    pt: 'pt',
    sp: 'es',
    ar: 'ar',
    ps: 'fa',
    de: 'de',
    fr: 'fr',
    it: 'it'
  };

  readonly selectedLang$ = new BehaviorSubject<TranslateLangCode>(this.getInitialLang());

  constructor(private readonly ngZone: NgZone) {}

  bootstrap(router: Router): void {
    if (this.bootstrapInitialized) {
      return;
    }
    this.bootstrapInitialized = true;

    this.waitForCombo(3000).then((found) => {
      if (found) {
        this.applyLanguage(this.selectedLang$.value);
      }
    });

    router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        this.ngZone.onStable.pipe(take(1)).subscribe(() => {
          this.applyLanguage(this.selectedLang$.value);
        });
      });
  }

  setLanguage(code: TranslateLangCode): void {
    const googleCode = this.googleLangMap[code];
    this.selectedLang$.next(code);
    localStorage.setItem(this.storageKey, code);
    this.setGoogtransCookie(googleCode);

    this.waitForCombo(1500).then((found) => {
      if (found) {
        this.applyLanguage(code);
        return;
      }
      window.location.reload();
    });
  }

  private getInitialLang(): TranslateLangCode {
    const fromStorage = localStorage.getItem(this.storageKey) as TranslateLangCode | null;
    if (fromStorage && this.googleLangMap[fromStorage]) {
      return fromStorage;
    }

    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('ru')) {
      return 'ru';
    }
    return 'en';
  }

  private applyLanguage(code: TranslateLangCode): void {
    const googleCode = this.googleLangMap[code];
    const globalWindow = window as any;

    this.setGoogtransCookie(googleCode);

    if (typeof globalWindow.doGTranslate === 'function') {
      globalWindow.doGTranslate(`${this.sourceLang}|${this.sourceLang}`);
      setTimeout(() => {
        globalWindow.doGTranslate(`${this.sourceLang}|${googleCode}`);
      }, 0);
      return;
    }

    const combo = document.querySelector('select.goog-te-combo') as HTMLSelectElement | null;
    if (!combo) {
      return;
    }

    combo.value = googleCode;
    combo.dispatchEvent(new Event('change'));
    combo.dispatchEvent(new Event('change'));
  }

  private setGoogtransCookie(targetGoogleCode: string): void {
    const cookieValue = `/${this.sourceLang}/${targetGoogleCode}`;
    document.cookie = `googtrans=${cookieValue};path=/;max-age=${this.cookieMaxAge}`;

    if (location.hostname.includes('.')) {
      document.cookie = `googtrans=${cookieValue};path=/;max-age=${this.cookieMaxAge};domain=.${location.hostname.replace(/^www\./, '')}`;
    }
  }

  private waitForCombo(timeoutMs: number): Promise<boolean> {
    const stepMs = 100;
    const started = Date.now();

    return new Promise((resolve) => {
      const check = () => {
        const hasCombo = !!document.querySelector('select.goog-te-combo');
        if (hasCombo) {
          resolve(true);
          return;
        }
        if (Date.now() - started >= timeoutMs) {
          resolve(false);
          return;
        }
        setTimeout(check, stepMs);
      };
      check();
    });
  }
}
